<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";

import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";
import { useEnum } from "@/stores/project/project.enum";
import { declaredUnitSymbol } from "@/utils/declaredUnitSymbol";
import { formatNumber } from "@/services/Utils/DecimalFormatter";

// Always-visible header KPIs for the material modal:
//  - Total GWP in the calculation-type unit (RTLCA-5027)
//  - The quantity used for the calculation (RTLCA-5029)
const props = defineProps<{
  material: SpeckleMaterial;
  // Total GWP for this material, already normalized to gwpUnit, resolved from the dashboard
  // per-material breakdown in MaterialDetailView on the backend's material-total definition — every
  // module except B6 and D — so it equals the Emissions tab's Project TOTAL. Null when unavailable →
  // rendered as "-".
  totalGwp: number | null;
  // One sentence naming the global Stages filter when it is narrowing `totalGwp` — the figure is
  // scoped to the same selection the Emissions tab's Project series is (RTLCA-5397). Null/absent when
  // nothing is narrowed, and the tooltip says nothing about scope.
  stageScopeNote?: string | null;
  gwpUnit: string;
}>();

const { declaredUnits } = storeToRefs(useEnum());

const preferredUnit = computed(() => props.material.totalDeclaredUnits?.find((unit) => unit.preferred) ?? null);

const quantityValue = computed(() => {
  const unit = preferredUnit.value;
  if (!unit) return null;
  return unit.newValue ?? unit.value;
});

const quantitySymbol = computed(() => {
  const unit = preferredUnit.value;
  if (!unit) return "";
  return declaredUnitSymbol(declaredUnits.value[unit.declaredUnit]);
});

const gwpDisplay = computed(() => (props.totalGwp != null ? formatNumber(props.totalGwp) : "-"));

// The figure is the material's PROJECT-level contribution — quantity-scaled and divided by the
// calculation type's denominator (kgCO₂-eq/m²/year for BR18). The Emissions tab offers that basis and
// the mapped EPD's own declared values side by side behind a Project/EPD switch, so an unqualified
// "Total GWP" up here invited reading it against whichever of the two happened to be on screen: a
// project figure in kgCO₂-eq/m²/year next to an EPD figure in kg CO₂e/m³ looks like a broken number
// rather than a different question (RTLCA-5361). The label names the basis and the hint spells it out.
const totalGwpTooltip = computed(() => {
  const basis = props.gwpUnit ? `, in the project's reporting unit (${props.gwpUnit})` : "";
  return [
    `This material's contribution to the project total${basis}.`,
    "Modules B6 and D are shown on the Emissions tab but not counted here.",
    props.stageScopeNote,
    "Switch that tab to EPD to see the mapped EPD's own declared values, per declared unit.",
  ]
    .filter(Boolean)
    .join(" ");
});
</script>

<template>
  <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
    <!-- One tooltip target for the whole KPI, so the hint is reachable from the label, the value and
         the unit alike rather than only from whichever span the pointer happened to land on. -->
    <span
      v-tooltip="totalGwpTooltip"
      class="flex items-baseline gap-x-2">
      <span class="t-caption text-type-muted tracking-wide uppercase">Total GWP (project)</span>
      <span class="t-sh4 text-app-blue-600">{{ gwpDisplay }}</span>
      <span class="t-caption text-type-muted">{{ gwpUnit }}</span>
    </span>

    <span
      class="mx-2 text-slate-300"
      aria-hidden="true">
      |
    </span>

    <span class="t-caption text-type-muted tracking-wide uppercase">Quantity</span>
    <span class="t-small-bold text-type-default">
      {{ quantityValue !== null ? formatNumber(quantityValue) : "—" }}
    </span>
    <span class="t-caption text-type-muted">{{ quantitySymbol }}</span>
  </div>
</template>
